// src/features/display/useDisplayAudio.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { getSoundEffect, checkSoundExists } from '../../services/db';

export const useDisplayAudio = (isPrimaryDisplay = false) => {
  const [isMutedByPrimary, setIsMutedByPrimary] = useState(false);
  const audioContextRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const lastHeartbeatTimeRef = useRef(0);
  const broadcastChannelRef = useRef(null);

  // BroadcastChannel Setup for Sound Coordination
  useEffect(() => {
    if (typeof BroadcastChannel !== 'function') return;

    const channel = new BroadcastChannel('blind_pace_sound_coordination');
    broadcastChannelRef.current = channel;

    if (isPrimaryDisplay) {
      const sendHeartbeat = () => {
        channel.postMessage({ type: 'DISPLAY_ACTIVE', timestamp: Date.now() });
      };
      sendHeartbeat();
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 2000);
    } else {
      channel.onmessage = (event) => {
        if (event.data?.type === 'DISPLAY_ACTIVE') {
          lastHeartbeatTimeRef.current = Date.now();
          setIsMutedByPrimary(prev => {
            if (prev) return prev;
            return true;
          });
        }
      };

      heartbeatIntervalRef.current = setInterval(() => {
        if (Date.now() - lastHeartbeatTimeRef.current > 4000) {
          setIsMutedByPrimary(prev => {
            if (!prev) return prev;
            return false;
          });
        }
      }, 1000);
    }

    return () => {
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (broadcastChannelRef.current) broadcastChannelRef.current.close();
    };
  }, [isPrimaryDisplay]);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        return null;
      }
    }
    return audioContextRef.current;
  }, []);

  const playSound = useCallback(async (soundId, durationLimit, onEndedCallback, force = false) => {
    if (isMutedByPrimary && !force) {
      if (typeof onEndedCallback === 'function') onEndedCallback();
      return;
    }

    const audioCtx = getAudioContext();
    if (!audioCtx) {
      if (typeof onEndedCallback === 'function') onEndedCallback();
      return;
    }

    if (audioCtx.state === 'suspended') {
      try {
        await audioCtx.resume();
      } catch (resumeError) {
        if (typeof onEndedCallback === 'function') onEndedCallback();
        return;
      }
    }

    try {
      const soundData = await getSoundEffect(soundId);
      if (!soundData?.file) {
        if (typeof onEndedCallback === 'function') onEndedCallback();
        return;
      };

      const arrayBuffer = await soundData.file.arrayBuffer();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      const source = audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioCtx.destination);

      if (typeof onEndedCallback === 'function') {
        source.onended = onEndedCallback;
      }

      source.start(0);

      if (durationLimit > 0) {
        setTimeout(() => {
          try { source.stop(); } catch (e) { /* ignore */ }
        }, durationLimit * 1000);
      }
    } catch (error) {
      if (typeof onEndedCallback === 'function') onEndedCallback();
    }
  }, [getAudioContext, isMutedByPrimary]);

  return {
    isMutedByPrimary,
    getAudioContext,
    playSound,
    checkSoundExists
  };
};
